import {
  PRODUCT_EVENT_PROPERTY_ALLOWLIST,
  type ProductEventInput,
} from '@/lib/telemetry/product-events'

type ProductPrimitive = string | number | boolean | null
type ProductProperties = Record<string, ProductPrimitive>

const MAX_STRING_LENGTH = 120

const EXPLICITLY_BLOCKED_FIELDS = new Set([
  'message',
  'notes',
  'note',
  'body',
  'content',
  'token',
  'tokens',
  'headers',
  'header',
  'cookie',
  'cookies',
  'authorization',
])

function normalizeKey(key: string): string {
  return key
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '_')
}

function toPrimitive(value: unknown): ProductPrimitive | undefined {
  if (typeof value === 'string') {
    const trimmed = value.replace(/\s+/g, ' ').trim()
    if (!trimmed) return undefined
    return trimmed.length > MAX_STRING_LENGTH
      ? trimmed.slice(0, MAX_STRING_LENGTH)
      : trimmed
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined
  }

  if (typeof value === 'boolean') {
    return value
  }

  if (value === null) {
    return null
  }

  return undefined
}

function normalizeErrorFingerprint(error: unknown): {
  errorClass: string
  errorCode: string
} {
  const normalizeErrorCode = (value: unknown): string | null => {
    if (typeof value === 'string') {
      const normalized = normalizeKey(value)
      return normalized.length > 0 ? normalized : null
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(Math.trunc(value))
    }

    return null
  }

  const normalizeErrorClass = (value: unknown): string | null => {
    if (typeof value !== 'string') return null

    const trimmed = value.trim()
    if (trimmed.length === 0) return null
    return trimmed.length > MAX_STRING_LENGTH
      ? trimmed.slice(0, MAX_STRING_LENGTH)
      : trimmed
  }

  if (!error) {
    return {
      errorClass: 'UnknownError',
      errorCode: 'unknown',
    }
  }

  if (error instanceof Error) {
    const maybeCode = error as Error & { code?: unknown }
    const errorCode =
      typeof maybeCode.code === 'string'
        ? maybeCode.code.toLowerCase()
        : (error.name || 'error').toLowerCase()

    return {
      errorClass: error.name || 'Error',
      errorCode,
    }
  }

  if (typeof error === 'object') {
    const err = error as Record<string, unknown>
    const errorCodeRaw =
      err.code ??
      err.error_code ??
      err.errorCode ??
      err.status_code ??
      err.statusCode ??
      err.status
    const errorClassRaw =
      err.name ??
      err.error_name ??
      err.errorName ??
      err.type ??
      err.error_type
    const ctorName =
      error && (error as { constructor?: { name?: unknown } }).constructor?.name !== 'Object'
        ? (error as { constructor?: { name?: unknown } }).constructor?.name
        : null

    return {
      errorClass: normalizeErrorClass(errorClassRaw) ?? normalizeErrorClass(ctorName) ?? 'object',
      errorCode:
        normalizeErrorCode(errorCodeRaw) ??
        normalizeErrorCode(errorClassRaw) ??
        normalizeErrorCode(ctorName) ??
        'unknown',
    }
  }

  return {
    errorClass: typeof error,
    errorCode: 'unknown',
  }
}

function hashFallback(value: string): string {
  let hash = 0
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash).toString(16).padStart(8, '0')
}

async function hashValue(value: string): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    return hashFallback(value)
  }

  const bytes = new TextEncoder().encode(value)
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

function isBlockedFreeTextField(key: string): boolean {
  if (EXPLICITLY_BLOCKED_FIELDS.has(key)) {
    return true
  }

  return (
    key.includes('message') ||
    key.includes('note') ||
    key.includes('body') ||
    key.includes('content') ||
    key.includes('token') ||
    key.includes('header') ||
    key.includes('cookie')
  )
}

async function hashIdentifier(
  out: ProductProperties,
  key: string,
  value: string | number,
): Promise<void> {
  void key
  out.entity_id_hash = await hashValue(String(value))
}

export async function sanitizeProductEventProperties(
  input: ProductEventInput,
): Promise<ProductProperties> {
  const raw: Record<string, unknown> = {
    action: input.action,
    result: input.result,
    surface: input.surface,
    ...(input.properties ?? {}),
  }

  if (input.entityId !== undefined && input.entityId !== null) {
    raw.entity_id = input.entityId
  }

  if (input.result === 'failure' || input.error) {
    const fingerprint = normalizeErrorFingerprint(input.error)
    raw.error_class = fingerprint.errorClass
    raw.error_code = fingerprint.errorCode
  }

  const out: ProductProperties = {}

  for (const [key, value] of Object.entries(raw)) {
    const normalizedKey = normalizeKey(key)
    if (!normalizedKey) continue
    if (isBlockedFreeTextField(normalizedKey)) continue

    const primitive = toPrimitive(value)
    if (primitive === undefined) continue

    if (
      typeof primitive !== 'boolean' &&
      primitive !== null &&
      (normalizedKey === 'entity_id' ||
        normalizedKey === 'id' ||
        normalizedKey.endsWith('_id') ||
        normalizedKey.includes('email') ||
        normalizedKey.includes('phone'))
    ) {
      await hashIdentifier(out, normalizedKey, primitive)
      continue
    }

    if (PRODUCT_EVENT_PROPERTY_ALLOWLIST.has(normalizedKey)) {
      out[normalizedKey] = primitive
    }
  }

  return out
}
